import { Shield, Lock, CheckCircle, AlertCircle, User, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { BroUser } from './types';
import { useJobBoardAccess } from '@/hooks/useJobBoardAccess';
import VerificationStatus from './VerificationStatus';
import VerificationPrompt from './VerificationPrompt';

interface JobBoardAccessGateProps {
  user: BroUser | null;
  requiredLevel: 'browse' | 'apply' | 'post';
  onVerificationComplete?: () => void;
  children: React.ReactNode;
}

const JobBoardAccessGate: React.FC<JobBoardAccessGateProps> = ({
  user,
  requiredLevel,
  onVerificationComplete,
  children
}) => {
  const { access, loading, error, getVerificationSteps } = useJobBoardAccess(user);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tan"></div>
        <span className="ml-2 text-tan">Checking access permissions...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert className="mx-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Unable to verify access permissions: {error}
        </AlertDescription>
      </Alert>
    );
  }

  // Check if user has required access level
  const hasAccess = () => {
    switch (requiredLevel) {
      case 'browse':
        return access.canBrowseJobs;
      case 'apply':
        return access.canApplyToJobs;
      case 'post':
        return access.canPostJobs;
      default:
        return false;
    }
  };

  // If user has access, render children
  if (hasAccess()) {
    return <>{children}</>;
  }

  // If user doesn't have access, show verification gate
  return (
    <div className="p-4 space-y-4">
      {/* Access Level Header */}
      <Card className="bg-navy-light border-tan-dark">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            {requiredLevel === 'browse' && <User className="text-tan" size={32} />}
            {requiredLevel === 'apply' && <Shield className="text-tan" size={32} />}
            {requiredLevel === 'post' && <Star className="text-tan" size={32} />}
          </div>
          <CardTitle className="text-tan">
            {requiredLevel === 'browse' && 'Browse Jobs'}
            {requiredLevel === 'apply' && 'Apply to Jobs'}
            {requiredLevel === 'post' && 'Post Jobs'}
          </CardTitle>
          <CardDescription className="text-tan-dark">
            {requiredLevel === 'browse' && 'View available opportunities in the BroMentum ecosystem'}
            {requiredLevel === 'apply' && 'Apply for jobs and start earning in the BroMentum ecosystem'}
            {requiredLevel === 'post' && 'Post jobs and hire talented Bros for your projects'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Current Verification Level Badge */}
          <div className="flex justify-center mb-4">
            <Badge 
              variant={access.verificationLevel === 'none' ? 'destructive' : 'secondary'}
              className="px-3 py-1"
            >
              {access.verificationLevel === 'none' && 'Not Verified'}
              {access.verificationLevel === 'browse' && 'Browse Level'}
              {access.verificationLevel === 'apply' && 'Apply Level'}
              {access.verificationLevel === 'post' && 'Post Level'}
              {access.verificationLevel === 'full' && 'Full Access'}
            </Badge>
          </div>

          {/* User Status Overview */}
          {user ? (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center">
                <div className="text-tan font-bold text-lg">{user.bro_score || 0}</div>
                <div className="text-tan-dark text-xs">BroScore</div>
              </div>
              <div className="text-center">
                <div className="text-tan font-bold text-lg">{user.rank || 'Unranked'}</div>
                <div className="text-tan-dark text-xs">Rank</div>
              </div>
            </div>
          ) : (
            <Alert className="mb-6">
              <User className="h-4 w-4" />
              <AlertDescription>
                Please connect your account to access the job board.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Verification Requirements */}
      {user && access.missingRequirements.length > 0 && (
        <Card className="bg-navy-light border-tan-dark">
          <CardHeader>
            <CardTitle className="text-tan flex items-center">
              <Lock className="mr-2" size={20} />
              Verification Required
            </CardTitle>
            <CardDescription className="text-tan-dark">
              Complete the following requirements to access this feature:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {access.missingRequirements.map((requirement, index) => (
                <div key={index} className="flex items-center justify-between bg-navy rounded-lg p-3 border border-tan-dark">
                  <div className="flex items-center">
                    <AlertCircle className="text-orange-400 mr-2" size={16} />
                    <span className="text-tan text-sm">{requirement}</span>
                  </div>
                  <div className="text-tan-dark text-xs">Required</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verification Progress */}
      {user && (
        <VerificationStatus
          user={user}
          access={access}
          verificationSteps={getVerificationSteps()}
        />
      )}

      {/* Verification Actions */}
      {user && (
        <VerificationPrompt
          user={user}
          access={access}
          requiredLevel={requiredLevel}
          verificationSteps={getVerificationSteps()}
          onVerificationComplete={onVerificationComplete}
        />
      )}

      {/* What You Can Do Without Verification */}
      <Card className="bg-navy-light border-tan-dark">
        <CardHeader>
          <CardTitle className="text-tan text-sm">What you can do right now:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center text-tan-dark text-sm">
              <CheckCircle className="text-green-400 mr-2" size={16} />
              Browse public job listings
            </div>
            <div className="flex items-center text-tan-dark text-sm">
              <CheckCircle className="text-green-400 mr-2" size={16} />
              View job requirements and budgets
            </div>
            <div className="flex items-center text-tan-dark text-sm">
              <CheckCircle className="text-green-400 mr-2" size={16} />
              Learn about verification requirements
            </div>
            {!access.canApplyToJobs && (
              <div className="flex items-center text-tan-dark text-sm">
                <Lock className="text-red-400 mr-2" size={16} />
                Apply to jobs (requires verification)
              </div>
            )}
            {!access.canPostJobs && (
              <div className="flex items-center text-tan-dark text-sm">
                <Lock className="text-red-400 mr-2" size={16} />
                Post job listings (requires higher verification)
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Get Started Button for Non-Authenticated Users */}
      {!user && (
        <div className="text-center">
          <Button 
            className="bg-tan text-navy hover:bg-tan-dark"
            onClick={() => window.location.href = '/initiation'}
          >
            Get Started - Join BroMentum
          </Button>
        </div>
      )}

      {/* Benefits of Verification */}
      <Card className="bg-gradient-to-r from-purple-600/20 to-purple-800/20 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-tan text-sm flex items-center">
            <Star className="mr-2 text-yellow-400" size={16} />
            Benefits of Full Verification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="text-purple-200">✨ Access to premium job opportunities</div>
            <div className="text-purple-200">🛡️ Enhanced trust and credibility</div>
            <div className="text-purple-200">⚡ Priority application processing</div>
            <div className="text-purple-200">💎 Exclusive high-paying projects</div>
            <div className="text-purple-200">🎯 Direct employer messaging</div>
            <div className="text-purple-200">📈 Increased visibility in search results</div>
          </div>
        </CardContent>
      </Card>

      {/* Fallback: Show limited content for browse-only access */}
      {requiredLevel === 'browse' && access.canBrowseJobs && (
        <div className="mt-6">
          {children}
        </div>
      )}
    </div>
  );
};

export default JobBoardAccessGate;